/**
 * Three-scenario block (SPI-M-O convention).
 * CD R10 Q4 verdict: coarse, no false precision, visually distinct
 * from the R9 counterfactual divergence chart.
 *
 * Three forecast scenarios on a card-grid: best / central (planning case)
 * / reasonable-worst-case. Each card carries:
 *   - Coloured top border for the scenario kind
 *   - Mini sparkline of the forecast trajectory
 *   - Peak metric + when (e.g. "4.8k peak · wk 6")
 *   - PHIA likelihood + analytical confidence
 *
 * The CENTRAL scenario gets a "planning case" tag — visually distinct
 * background, so the operator's working assumption reads clearly.
 */

import type { ScenarioForecast } from './types';
import { PHIA_BANDS } from './types';

interface ThreeScenarioBlockProps {
  scenarios: readonly [ScenarioForecast, ScenarioForecast, ScenarioForecast];
}

export function ThreeScenarioBlock({ scenarios }: ThreeScenarioBlockProps) {
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10,
    }}>
      {scenarios.map(s => (
        <ScenarioCard key={s.kind} scenario={s} />
      ))}
    </div>
  );
}

function ScenarioCard({ scenario: s }: { scenario: ScenarioForecast }) {
  const color = s.kind === 'best'
    ? 'var(--bm-prov-confirmed, var(--bm-state-live))'
    : s.kind === 'central' ? 'var(--bm-threat-3, #fbbf24)'
    : 'var(--bm-threat-1, #f87171)';
  const phiaRange = PHIA_BANDS.find(b => b.word === s.phia)?.range ?? '';
  const spark = s.spark ?? [];
  const maxSpark = spark.length > 0 ? Math.max(...spark) : 1;
  const sparkPoints = spark.length > 0
    ? spark.map((v, i) => `${(i / Math.max(1, spark.length - 1)) * 100},${30 - (v / maxSpark) * 26 - 2}`).join(' ')
    : '';
  return (
    <div style={{
      background: s.isPlanningCase ? 'var(--bm-bg-subtle)' : 'var(--bm-bg-panel, var(--bm-bg))',
      border: '1px solid var(--bm-border)',
      borderTop: `3px solid ${color}`,
      borderRadius: 'var(--bm-radius-md)',
      padding: 12,
    }}>
      <div style={{
        fontSize: 12.5, fontWeight: 650,
        display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' as const,
      }}>
        {s.label}
        {s.isPlanningCase && (
          <span style={{
            fontFamily: 'var(--bm-font-mono)', fontSize: 8.5, fontWeight: 700,
            color: 'var(--bm-threat-3, #fbbf24)',
            border: '1px solid var(--bm-threat-3, #fbbf24)',
            borderRadius: 4, padding: '0 5px', letterSpacing: '.3px',
          }}>planning case</span>
        )}
      </div>
      {sparkPoints && (
        <svg viewBox="0 0 100 30" preserveAspectRatio="none" style={{ width: '100%', height: 34, marginTop: 6 }}>
          <polyline points={sparkPoints} fill="none" stroke={color} strokeWidth={1.6} vectorEffect="non-scaling-stroke" />
        </svg>
      )}
      <div style={{ fontSize: 12, color: 'var(--bm-text-muted)', marginTop: 4 }}>
        <span style={{
          color, fontFamily: 'var(--bm-font-mono)', fontWeight: 700,
        }}>{s.peak}</span> peak · {s.when}
      </div>
      <div style={{
        fontFamily: 'var(--bm-font-mono)', fontSize: 10.5,
        color: 'var(--bm-text-subtle)', marginTop: 3,
      }} title={phiaRange ? `${phiaRange} · ${s.confidence} confidence` : ''}>
        {s.phia} · conf {s.confidence}
      </div>
    </div>
  );
}
